import { AlertCircle, CheckCircle, Info, X } from 'lucide-react'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

type ToastType = 'success' | 'error' | 'info'

interface ToastAction {
  label: string
  onClick: () => void
}

interface ToastEntry {
  id: number
  message: string
  type: ToastType
  action: ToastAction | null
}

interface ToastApi {
  success: (msg: string, action?: ToastAction | null) => number
  error: (msg: string) => number
  info: (msg: string, action?: ToastAction | null) => number
  /** Explicit action toast with longer default duration. */
  withAction: (
    msg: string,
    action: ToastAction,
    opts?: { type?: ToastType; duration?: number },
  ) => number
}

const ToastContext = createContext<ToastApi | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])

  const addToast = useCallback(
    (
      message: string,
      type: ToastType = 'success',
      duration = 4000,
      action: ToastAction | null = null,
    ): number => {
      const id = Date.now() + Math.random()
      setToasts((prev) => [...prev, { id, message, type, action }])
      if (duration > 0) {
        setTimeout(
          () => setToasts((prev) => prev.filter((t) => t.id !== id)),
          duration,
        )
      }
      return id
    },
    [],
  )

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useMemo<ToastApi>(
    () => ({
      success: (msg, action = null) => addToast(msg, 'success', 4000, action),
      error: (msg) => addToast(msg, 'error', 6000),
      info: (msg, action = null) => addToast(msg, 'info', 4000, action),
      withAction: (
        msg,
        action,
        { type = 'success' as ToastType, duration = 6000 } = {},
      ) => addToast(msg, type, duration, action),
    }),
    [addToast],
  )

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            {t.type === 'success' && <CheckCircle size={16} />}
            {t.type === 'error' && <AlertCircle size={16} />}
            {t.type === 'info' && <Info size={16} />}
            <span>{t.message}</span>
            {t.action && (
              <button
                className="toast-action"
                onClick={() => {
                  t.action?.onClick?.()
                  removeToast(t.id)
                }}
              >
                {t.action.label}
              </button>
            )}
            <button className="toast-close" onClick={() => removeToast(t.id)}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
